/**
 * ConfigSchemaEditor - Select a registered schema or define inline
 *
 * Provides two modes:
 * 1. Select from registered schemas - stores a $ref reference
 * 2. Define schema inline (fallback/custom) - stores full schema
 *
 * Reference format:
 * { "$ref": "content:schema-uuid", "_schema_name": "SchemaName" }
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Code, ExternalLink, Loader2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { schemaApi, type SchemaItem, type JsonSchema } from '@/services/schemaApi';

interface ConfigSchemaEditorProps {
  /** JSON string of the schema (reference or inline) */
  value: string;
  /** Called with updated JSON string */
  onChange: (value: string) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Placeholder text for empty state */
  placeholder?: string;
}

/** Check if a parsed schema is a reference */
function isSchemaRef(schema: Record<string, unknown>): boolean {
  return typeof schema.$ref === 'string' && schema.$ref.startsWith('content:');
}

/** Extract schema ID from a $ref */
function getSchemaIdFromRef(ref: string): string | null {
  if (ref.startsWith('content:')) {
    return ref.slice(8); // Remove 'content:' prefix
  }
  return null;
}

/** Create a schema reference object */
function createSchemaRef(schemaId: string, schemaName: string): Record<string, unknown> {
  return {
    $ref: `content:${schemaId}`,
    _schema_name: schemaName,
  };
}

export function ConfigSchemaEditor({
  value,
  onChange,
  disabled = false,
  placeholder = 'No configuration parameters defined',
}: ConfigSchemaEditorProps) {
  const [mode, setMode] = useState<'select' | 'inline'>('select');
  const [schemas, setSchemas] = useState<SchemaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(null);
  const [schemaPreview, setSchemaPreview] = useState<JsonSchema | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load available schemas
  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        setLoading(true);
        const items = await schemaApi.list();
        setSchemas(items);
      } catch (err) {
        console.error('Failed to load schemas:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSchemas();
  }, []);

  // Parse current value to determine mode and selected schema on initial load
  useEffect(() => {
    if (initialized) return;

    try {
      const parsed = JSON.parse(value);

      if (isSchemaRef(parsed)) {
        // It's a schema reference
        const schemaId = getSchemaIdFromRef(parsed.$ref as string);
        if (schemaId) {
          setMode('select');
          setSelectedSchemaId(schemaId);
        }
      } else if (parsed.properties && Object.keys(parsed.properties).length > 0) {
        // It's an inline schema with properties
        setMode('inline');
      }
      // Otherwise default to select mode with no selection
    } catch {
      // Invalid JSON, default to select mode
    }

    setInitialized(true);
  }, [value, initialized]);

  // Load schema preview when selection changes (for display only)
  useEffect(() => {
    if (!selectedSchemaId) {
      setSchemaPreview(null);
      return;
    }

    const fetchSchemaContent = async () => {
      try {
        setLoadingPreview(true);
        const content = await schemaApi.getContent(selectedSchemaId);
        setSchemaPreview(content);
      } catch (err) {
        console.error('Failed to load schema content:', err);
        setSchemaPreview(null);
      } finally {
        setLoadingPreview(false);
      }
    };
    fetchSchemaContent();
  }, [selectedSchemaId]);

  // Parse current value for display
  const parsedValue = useMemo(() => {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [value]);

  // Get schema name from reference or preview
  const schemaName = useMemo(() => {
    if (parsedValue && isSchemaRef(parsedValue)) {
      return parsedValue._schema_name as string | undefined;
    }
    // Try to find name from schemas list
    if (selectedSchemaId) {
      const schema = schemas.find(s => s.id === selectedSchemaId);
      return schema?.name;
    }
    return undefined;
  }, [parsedValue, selectedSchemaId, schemas]);

  // Count properties (from preview for refs, from value for inline)
  const propertyCount = useMemo(() => {
    if (schemaPreview) {
      return Object.keys(schemaPreview.properties || {}).length;
    }
    if (parsedValue && !isSchemaRef(parsedValue)) {
      return Object.keys((parsedValue.properties as Record<string, unknown>) || {}).length;
    }
    return 0;
  }, [schemaPreview, parsedValue]);

  const handleSchemaSelect = (schemaId: string) => {
    if (schemaId === '__inline__') {
      setMode('inline');
      setSelectedSchemaId(null);
      setSchemaPreview(null);
      // Reset to empty schema if switching to inline
      const currentParsed = parsedValue;
      if (!currentParsed || isSchemaRef(currentParsed) || Object.keys(currentParsed.properties || {}).length === 0) {
        onChange(JSON.stringify({ type: 'object', properties: {} }, null, 2));
      }
    } else {
      setMode('select');
      setSelectedSchemaId(schemaId);

      // Find schema name and store reference
      const schema = schemas.find(s => s.id === schemaId);
      if (schema) {
        const ref = createSchemaRef(schemaId, schema.name);
        onChange(JSON.stringify(ref, null, 2));
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* Schema selector */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Config Schema</Label>
          <div className="flex items-center gap-2">
            {parsedValue && isSchemaRef(parsedValue) && (
              <Badge variant="outline" className="text-xs gap-1">
                <Link2 className="h-3 w-3" />
                Reference
              </Badge>
            )}
            {propertyCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {propertyCount} param{propertyCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading schemas...
          </div>
        ) : (
          <Select
            value={mode === 'inline' ? '__inline__' : selectedSchemaId || ''}
            onValueChange={handleSchemaSelect}
            disabled={disabled}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Select a schema..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__inline__">
                <span className="flex items-center gap-2">
                  <Code className="h-3 w-3" />
                  Define inline...
                </span>
              </SelectItem>
              {schemas.map((schema) => (
                <SelectItem key={schema.id} value={schema.id}>
                  {schema.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Schema preview/editor */}
      {mode === 'inline' ? (
        <div className="space-y-1">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="font-mono text-xs"
            rows={6}
            disabled={disabled}
            placeholder='{"type": "object", "properties": {"param1": {"type": "string"}}}'
          />
          <p className="text-[10px] text-muted-foreground">
            Define JSON Schema for config parameters
          </p>
        </div>
      ) : selectedSchemaId ? (
        <div className="space-y-2">
          {loadingPreview ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading schema...
            </div>
          ) : schemaPreview ? (
            <>
              {/* Schema name badge */}
              {schemaName && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Using:</span>
                  <Badge variant="default" className="text-xs">
                    {schemaName}
                  </Badge>
                </div>
              )}

              {schemaPreview.description && (
                <p className="text-xs text-muted-foreground">
                  {schemaPreview.description}
                </p>
              )}
              <div className="text-xs space-y-1 border rounded-md p-2 bg-muted/30">
                <p className="font-medium text-muted-foreground">Properties:</p>
                {Object.entries(schemaPreview.properties || {}).map(([name, prop]) => (
                  <div key={name} className="flex items-center gap-2">
                    <code className="text-primary">{name}</code>
                    <Badge variant="outline" className="text-[10px] h-4">
                      {prop.type}
                    </Badge>
                    {schemaPreview.required?.includes(name) && (
                      <span className="text-red-500 text-[10px]">required</span>
                    )}
                  </div>
                ))}
                {Object.keys(schemaPreview.properties || {}).length === 0 && (
                  <p className="text-muted-foreground italic">No properties defined</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => window.open(`/developer/schemas/${selectedSchemaId}`, '_blank')}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Edit Schema
              </Button>
            </>
          ) : (
            <div className="text-center py-3 text-sm text-muted-foreground border border-dashed rounded-md">
              Failed to load schema
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-3 text-sm text-muted-foreground border border-dashed rounded-md">
          {placeholder}
        </div>
      )}
    </div>
  );
}

export default ConfigSchemaEditor;
